import {parseNumber} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {MeasurementUnit} from 'constants/__generated/MeasurementUnit';
import {AnalyticsMetadataForContext} from 'constants/AnalyticsConstants';
import {LegacyMeasurement, Measurement, NULL_UNITS} from 'constants/MeasurementConstants';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import HelpToolTip from 'help/HelpTooltip';
import {SampleStatusWarningTrackingParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import {FILTER_PREDICATES_BY_MEASUREMENT_TYPE, MeasurementType} from 'samples/MeasurementForm';
import BaseBulkEditSamplesModal from 'samples/toolbar/BaseBulkEditSamplesModal';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import FormInput from 'ui-library/bs/FormInput';
import Row from 'ui-library/bs/Row';
import ErrorMessage from 'ui-library/ErrorMessage';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import SearchableUnitDropdown from 'units/SearchableUnitDropdown';
import {capitalize} from 'util/NameUtil';
import {InputChangeEvent} from 'util/Types';

function getCurrentMeasurement(measurements: Array<Partial<Measurement>>): string {
  const firstValue: number | null = measurements[0].value ?? null;
  const firstUnit: MeasurementUnit | null = measurements[0].unit ?? null;
  let currentMeasurement;
  if (
    measurements.every(
      (measurement) =>
        (measurement.value ?? null) === firstValue &&
        (measurement.unit?.id ?? null) === (firstUnit?.id ?? null)
    )
  ) {
    currentMeasurement = firstValue == null ? 'Not specified' : `${firstValue} ${firstUnit?.symbol}`;
  } else {
    currentMeasurement = 'Multiple values';
  }
  return currentMeasurement;
}
function getDefaultMeasurement(measurements: Array<Partial<Measurement>>): {
  defaultUnit: MeasurementUnit | null;
  defaultValue: string;
} {
  const firstUnit = measurements[0].unit ?? null;
  const firstValue = measurements[0].value;
  const defaultUnit = measurements.every((measurement) => measurement.unit?.id === firstUnit?.id)
    ? firstUnit
    : null;
  const defaultValue =
    defaultUnit != null && measurements.every((measurement) => measurement.value === firstValue)
      ? `${firstValue}`
      : '';
  return {defaultUnit, defaultValue};
}

export interface MeasurementEditorModalContentProps {
  label: string;
  labelPlural: string;
  measurements: Array<Partial<Measurement>>;
  measurementType: MeasurementType;
  shouldClearMeasurement: boolean;
  isValueRequired: boolean;
  value: string;
  units: string | null;
  unitId: string | null;
  valueError: string | null;
  unitsError: string | null;
  noChangeError: string | null;
  onChangeShouldClearMeasurement: (newIsClearValuesChecked: boolean) => void;
  onChangeValue: (newValue: string) => void;
  onChangeUnits: (newUnits: string | null) => void;
  onChangeUnitId: (newUnitId: string | null) => void;
}

export function MeasurementEditorModalContent(props: MeasurementEditorModalContentProps): JSX.Element {
  const currentQuantity = getCurrentMeasurement(props.measurements);

  const tooltipText =
    'This will only set new units for the selected containers. ' +
    `It won’t perform unit conversions on any existing ${props.labelPlural}.`;

  return (
    <>
      <Row>
        <Col md={4}>
          <FieldLabel label={`Current ${props.label}`} labelStyle={FieldLabel.STYLES.LABEL} />
          <div id="current-measurement" className={css(styles.currentValue)}>
            {currentQuantity}
          </div>
        </Col>
        <FormGroup>
          <Col md={4}>
            <FieldLabel
              label={`New ${props.label}`}
              labelStyle={FieldLabel.STYLES.LABEL}
              isRequired={!props.shouldClearMeasurement && props.isValueRequired}
            />
            <FormInput
              placeholder={props.shouldClearMeasurement ? 'Not specified' : 'Value'}
              error={(props.valueError || props.noChangeError) ?? undefined}
              disabled={props.shouldClearMeasurement}
              onChange={(e: InputChangeEvent) => {
                props.onChangeValue(e.target.value);
              }}
              value={props.value}
              dataTestAttrs={{component: 'MeasurementEditorModal', element: 'valueInput'}}
            />
            {props.valueError != null && <ErrorMessage error={props.valueError} />}
          </Col>
          <Col md={4}>
            <FieldLabel
              label="New units"
              labelStyle={FieldLabel.STYLES.LABEL}
              isRequired={!props.shouldClearMeasurement}
            />
            {!props.isValueRequired && <HelpToolTip className="u-mini-spacing-left" text={tooltipText} />}
            <SearchableUnitDropdown
              includeNone={true}
              disabled={props.shouldClearMeasurement}
              displaySelectionAsSymbol={true}
              selectedId={props.unitId}
              onSelect={(unit) => {
                props.onChangeUnitId(unit?.id ?? null);
                props.onChangeUnits(unit?.symbol ?? null);
              }}
              unitTypeFilter={FILTER_PREDICATES_BY_MEASUREMENT_TYPE[props.measurementType]}
            />
            {props.unitsError != null && <ErrorMessage error={props.unitsError} />}
          </Col>
        </FormGroup>
      </Row>
      {props.noChangeError != null && (
        <Row>
          <Col md={4} />
          <Col md={8}>
            <ErrorMessage error={props.noChangeError} />
          </Col>
        </Row>
      )}
      {currentQuantity !== 'Not specified' && (
        <Row>
          <Col md={6}>
            <CheckboxInput
              checked={props.shouldClearMeasurement}
              id="clear-values-and-units"
              label={`Clear ${props.labelPlural} and units`}
              onChange={(e: InputChangeEvent) => {
                props.onChangeShouldClearMeasurement(e.target.checked);
              }}
            />
          </Col>
        </Row>
      )}
    </>
  );
}

export interface MeasurementEditorModalSubmitArgs {
  newMeasurement: LegacyMeasurement;
  selectedContainerIds: Array<string>;
}

export interface MeasurementEditorModalProps {
  queryResultType: QueryResultType;
  measurements: Array<Partial<Measurement>>;
  measurementType: MeasurementType;
  onRequestSubmit: (args: MeasurementEditorModalSubmitArgs) => void;
  onRequestClose: () => void;
  label: string;
  labelPlural: string;
  sampleStatusWarningParams: SampleStatusWarningTrackingParams;
  itemIds: Array<string>;
  analyticsMetadata: AnalyticsMetadataForContext;
}

export default function MeasurementEditorModal(props: MeasurementEditorModalProps): JSX.Element {
  let defaultValue = '';
  let defaultUnit: MeasurementUnit | null = null;
  let isQuantityRequired = true;

  if (props.measurements.length !== 0) {
    ({defaultUnit, defaultValue} = getDefaultMeasurement(props.measurements));
    isQuantityRequired = props.measurements.some(({value}) => value == null);
  }

  const [shouldClearMeasurement, setShouldClearMeasurement] = useState<boolean>(false);
  const [value, setValue] = useState<string>(defaultValue);
  const [units, setUnits] = useState<string | null>(defaultUnit?.symbol ?? null);
  const [unitId, setUnitId] = useState<string | null>(defaultUnit?.id ?? null);
  const [valueError, setValueError] = useState<string | null>(null);
  const [unitsError, setUnitsError] = useState<string | null>(null);
  const [noChangeError, setNoChangeError] = useState<string | null>(null);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);
  useEffect(() => {
    setUnits(defaultUnit?.symbol ?? null);
    setUnitId(defaultUnit?.id ?? null);
  }, [defaultUnit]);

  const isInputValid = (): boolean => {
    let isValid = true;
    if (shouldClearMeasurement) {
      return true;
    }
    if (units != null && value === defaultValue && units === defaultUnit?.symbol) {
      setNoChangeError(
        `Please enter a new ${props.label} or units that are different from the current values`
      );
      isValid = false;
    } else if (value !== '') {
      const numericQuantity = parseNumber(value);
      if (numericQuantity == null || numericQuantity < 0) {
        setValueError(`${capitalize(props.label)} must be a non-negative numeric value`);
        isValid = false;
      }
    } else if (isQuantityRequired) {
      setValueError(`${capitalize(props.label)} is required`);
      isValid = false;
    }
    if (units == null) {
      setUnitsError('Units are required');
      isValid = false;
    }
    return isValid;
  };

  const handleRequestSubmit = (selectedSampleIds: Array<string>): void => {
    if (isInputValid()) {
      if (shouldClearMeasurement) {
        props.onRequestSubmit({
          newMeasurement: {value: null, units: null},
          selectedContainerIds: selectedSampleIds,
        });
      } else {
        const numericQuantity = parseNumber(value);
        props.onRequestSubmit({
          newMeasurement: {value: numericQuantity, units},
          selectedContainerIds: selectedSampleIds,
        });
      }
    }
  };

  const measurementModalContent =
    props.queryResultType === QueryResultType.SUCCESS && props.measurements.length !== 0 ? (
      <MeasurementEditorModalContent
        label={props.label}
        labelPlural={props.labelPlural}
        measurementType={props.measurementType}
        measurements={props.measurements}
        shouldClearMeasurement={shouldClearMeasurement}
        isValueRequired={isQuantityRequired}
        value={value}
        units={units}
        unitId={unitId}
        valueError={valueError}
        unitsError={unitsError}
        noChangeError={noChangeError}
        onChangeShouldClearMeasurement={(newIsClearValuesChecked) => {
          setShouldClearMeasurement(newIsClearValuesChecked);
          if (newIsClearValuesChecked) {
            setValueError(null);
            setUnitsError(null);
            setNoChangeError(null);
            setValue('');
            setUnits(null);
            setUnitId(null);
          }
        }}
        onChangeValue={(newQuantity) => {
          setValue(newQuantity);
          setValueError(null);
          setNoChangeError(null);
        }}
        onChangeUnits={(newUnits) => {
          if (newUnits !== NULL_UNITS) {
            setUnits(newUnits);
          } else {
            setUnits(null);
          }
          setUnitsError(null);
          setNoChangeError(null);
        }}
        onChangeUnitId={setUnitId}
      />
    ) : props.queryResultType === QueryResultType.PENDING ? (
      <CenteredHorizontalThrobber />
    ) : props.queryResultType === QueryResultType.SUCCESS && props.measurements.length === 0 ? (
      <ErrorMessage error="You do not have permission to edit any of the selected items." />
    ) : (
      <ErrorMessage error="Could not load containers; please try again" />
    );

  return (
    <BaseBulkEditSamplesModal
      modalTitle={`Update ${props.label}`}
      itemIds={props.itemIds}
      analyticsMetadata={props.analyticsMetadata}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleRequestSubmit}
      modalContent={measurementModalContent}
      sampleStatusParams={{trackingParams: props.sampleStatusWarningParams}}
      dataTestAttrs={{component: 'MeasurementEditorModal', element: 'root'}}
    />
  );
}
const styles = StyleSheet.create({
  currentValue: {paddingTop: '5px'},
  formPadding: {paddingTop: '15px'},
});
